import api from './api';
import type { IEnrollment, IPopulatedEnrollment } from '../types/enrollment';

/**
 * Payload for enrolling a student in a course.
 */
interface EnrollStudentPayload {
  studentId: string;
  courseId: string;
  batchId: string; // ✅ FIX: batchId is now required, not optional.
}

/**
 * Service function to enroll a student in a course and batch.
 */
export const enrollStudent = async (payload: EnrollStudentPayload): Promise<{ message: string; enrollment: IEnrollment }> => {
  try {
    const response = await api.post<{ message: string; enrollment: IEnrollment }>('/enrollments/enroll', payload);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to enroll student.');
  }
};

/**
 * Service function to get courses the authenticated student is enrolled in.
 */
export const getMyEnrollments = async (): Promise<IPopulatedEnrollment[]> => {
  try {
    const response = await api.get<IPopulatedEnrollment[]>('/enrollments/my-courses');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch your enrolled courses.');
  }
};

/**
 * Service function to get all enrollments for a specific student.
 */
export const getStudentEnrollments = async (studentId: string): Promise<IPopulatedEnrollment[]> => {
  try {
    const response = await api.get<IPopulatedEnrollment[]>(`/enrollments/student/${studentId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch student enrollments.');
  }
};

/**
 * Service function to get all students enrolled in a specific course.
 */
export const getEnrollmentsByCourse = async (courseId: string): Promise<IPopulatedEnrollment[]> => {
  try {
    const response = await api.get<IPopulatedEnrollment[]>(`/enrollments/course/${courseId}/students`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch course enrollments.');
  }
};

/**
 * Service function to update an enrollment's completion status.
 */
export const updateEnrollmentStatus = async (enrollmentId: string, completionStatus: 'in-progress' | 'completed' | 'dropped'): Promise<{ message: string; enrollment: IEnrollment }> => {
  try {
    const response = await api.patch<{ message: string; enrollment: IEnrollment }>(`/enrollments/${enrollmentId}/status`, { completionStatus });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update enrollment status.');
  }
};

/**
 * Service function to delete/de-enroll a student.
 */
export const deleteEnrollment = async (enrollmentId: string): Promise<string> => {
  try {
    const response = await api.delete<{ message: string }>(`/enrollments/${enrollmentId}`);
    return response.data.message;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete enrollment.');
  }
};

const enrollmentService = {
    enrollStudent,
    getMyEnrollments,
    getStudentEnrollments,
    getEnrollmentsByCourse,
    updateEnrollmentStatus,
    deleteEnrollment
};

export default enrollmentService;